/**
 * Playful one-liners shown under the page-level brand loader, picked by module.
 *
 * Each group maps route prefixes to a few short lines. The most specific
 * (longest) matching prefix wins, so `/agent-atlas/finops` reads as FinOps, not
 * Agent Atlas. Matching is segment-aware: `/security` matches `/security` and
 * `/security/incidents`, never `/security-atlas` (that has its own entry).
 *
 * Copy lives in the commonUi catalog under `commonBrandloader.lines.<group>.<key>`;
 * the English text here is the defaultValue used until the catalog loads (the
 * route-level Suspense fallback can paint before commonUi arrives), so the
 * loader never flashes a raw i18n key.
 *
 * Lines are only for page-level loaders. Cards, buttons and table rows show the
 * mark alone, and nothing here is ever shown after an error.
 */
export const LOADING_LINE_GROUPS = [
  {
    group: 'agentAtlas',
    prefixes: ['/agent-atlas'],
    lines: {
      l1: 'Rounding up your agents…',
      l2: 'Checking who owns what…',
      l3: 'Taking a head count of your AI…',
    },
  },
  {
    group: 'security',
    prefixes: ['/security-atlas', '/security', '/signals', '/incidents', '/detections', '/response', '/playbooks', '/action-center'],
    lines: {
      l1: 'Following the signals…',
      l2: 'Connecting the dots…',
      l3: 'Separating noise from threats…',
    },
  },
  {
    group: 'hunt',
    prefixes: ['/hunt'],
    lines: {
      l1: 'Sifting through the logs…',
      l2: 'Following the signals…',
    },
  },
  {
    group: 'contextGraph',
    prefixes: ['/agent-network', '/agent-atlas/agent-network', '/saas-ai/agent-network', '/security/attack-path'],
    lines: {
      l1: 'Asking the Context Graph…',
      l2: 'Tracing every connection…',
    },
  },
  {
    group: 'codeSecurity',
    prefixes: ['/sast'],
    lines: {
      l1: 'Reading your repos so you don’t have to…',
      l2: 'Looking for AI in your code…',
    },
  },
  {
    group: 'finops',
    prefixes: ['/finops', '/agent-atlas/finops'],
    lines: {
      l1: 'Counting every token…',
      l2: 'Following the money…',
    },
  },
  {
    group: 'shadowAi',
    prefixes: ['/shadow-ai', '/admin/shadow-ai-discovery'],
    lines: {
      l1: 'Looking behind the browser tabs…',
      l2: 'Finding the AI nobody mentioned…',
    },
  },
  {
    group: 'compliance',
    prefixes: ['/agent-atlas/compliance', '/agent-atlas/compliance-audit', '/admin/compliance'],
    lines: {
      l1: 'Lining up the evidence…',
      l2: 'Checking every control…',
    },
  },
  {
    group: 'cloud',
    prefixes: ['/cloud'],
    lines: {
      l1: 'Sweeping your clouds for AI…',
      l2: 'Checking every region…',
    },
  },
  {
    group: 'endpoint',
    prefixes: ['/endpoint-security'],
    lines: {
      l1: 'Checking in with every endpoint…',
      l2: 'Taking stock of the fleet…',
    },
  },
  {
    group: 'redTeam',
    prefixes: ['/red-team-scanner', '/red-team'],
    lines: {
      l1: 'Warming up the red team…',
      l2: 'Thinking like an attacker…',
    },
  },
  {
    group: 'customAgents',
    prefixes: ['/custom-agents', '/observability', '/user-behavior', '/admin/agents', '/intent-analysis', '/conversation-explorer', '/model-evaluation', '/admin/policies'],
    lines: {
      l1: 'Checking in on your agents…',
      l2: 'Timing every handoff…',
      l3: 'Reading the agents’ diaries…',
      l4: 'Asking your agents how their day went…',
    },
  },
  {
    group: 'gemini',
    prefixes: ['/gemini'],
    lines: {
      l1: 'Catching up with Gemini…',
      l2: 'Asking Gemini what it’s been up to…',
    },
  },
  {
    group: 'copilot',
    prefixes: ['/copilot'],
    lines: {
      l1: 'Catching up with Copilot…',
      l2: 'Peeking over Copilot’s shoulder…',
    },
  },
  {
    group: 'saasAi',
    prefixes: ['/saas-ai'],
    lines: {
      l1: 'Tallying every API call…',
      l2: 'Checking in on your AI providers…',
    },
  },
];

/** Short lines for section loaders (a card, panel, table or drawer filling in). */
export const SECTION_LINE_GROUP = {
  group: 'section',
  prefixes: [],
  lines: {
    l1: 'Crunching the numbers…',
    l2: 'Warming up the charts…',
    l3: 'Fetching the good stuff…',
    l4: 'Lining up the rows…',
    l5: 'Polishing the pixels…',
    l6: 'Herding the data points…',
  },
};

export const DEFAULT_LINE_GROUP = {
  group: 'default',
  prefixes: [],
  lines: {
    l1: 'Getting things ready…',
  },
};

const matches = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/** The line group for a pathname: the longest matching prefix wins. */
export const lineGroupFor = (pathname = '') => {
  const path = String(pathname || '').replace(/\/+$/, '') || '/';
  let best = null;
  let bestLen = -1;
  LOADING_LINE_GROUPS.forEach((entry) => {
    entry.prefixes.forEach((prefix) => {
      if (prefix.length > bestLen && matches(path, prefix)) {
        best = entry;
        bestLen = prefix.length;
      }
    });
  });
  return best || DEFAULT_LINE_GROUP;
};

/** [{ key, defaultValue }] for a pathname, ready for t(key, { defaultValue }). */
export const loadingLinesFor = (pathname) => {
  const entry = lineGroupFor(pathname);
  return Object.entries(entry.lines).map(([k, text]) => ({
    key: `commonUi:commonBrandloader.lines.${entry.group}.${k}`,
    defaultValue: text,
  }));
};

/** [{ key, defaultValue }] for section loaders. */
export const sectionLines = () => Object.entries(SECTION_LINE_GROUP.lines).map(([k, text]) => ({
  key: `commonUi:commonBrandloader.lines.section.${k}`,
  defaultValue: text,
}));
